import './bank-statement-modal.scss';

import { Button, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { DownloadOutlined } from '@/media/antd-icons';
import { DATE_FORMAT, SYMBOLS } from '@/shared/constants';
import { formatDate, formatPrice } from '@/shared/services/formatters';
import type { IBankStatement } from '@/shared/types/scoring-info.types';

import Modal from '../modal';
import { getStatementTotals } from './helpers/totals';

interface IProps {
  open: boolean;
  onClose: () => void;
  statement: IBankStatement;
}

/** Сумма в тенге: `1 240 000 ₸`. */
const toTenge = (amount: number): string =>
  `${formatPrice(amount)} ${SYMBOLS.tenge}`;

/** Дата периода выписки или тире. */
const toPeriodDate = (date?: string): string =>
  date ? dayjs(date).format(DATE_FORMAT) : SYMBOLS.longDash;

/** Разбор банковской выписки клиента: итоги, поступления, списания, остатки. */
const BankStatementModal: React.FC<IProps> = ({ open, onClose, statement }) => {
  const { t } = useTranslation();
  const isSuccess = statement.status === 'success';
  const { income, expenses } = getStatementTotals(statement);

  const subtitle = [
    statement.bank,
    statement.periodStart || statement.periodEnd
      ? t('scoringInfo.bankStatement.period', {
          from: toPeriodDate(statement.periodStart),
          to: toPeriodDate(statement.periodEnd)
        })
      : undefined,
    statement.parsedAt
      ? t('scoringInfo.bankStatement.parsedAtValue', {
          date: formatDate(statement.parsedAt)
        })
      : undefined
  ]
    .filter(Boolean)
    .join(' · ');

  const sections = [
    {
      title: t('scoringInfo.bankStatement.incomeSection'),
      rows: [
        [t('scoringInfo.bankStatement.topUp'), toTenge(statement.topUp)],
        [
          t('scoringInfo.bankStatement.ownIncome'),
          toTenge(statement.ownIncome)
        ],
        [t('scoringInfo.bankStatement.credit'), toTenge(statement.credit)]
      ]
    },
    {
      title: t('scoringInfo.bankStatement.expensesSection'),
      rows: [
        [
          t('scoringInfo.bankStatement.withdrawals'),
          toTenge(statement.withdrawals)
        ],
        [
          t('scoringInfo.bankStatement.transfersOut'),
          toTenge(statement.transfersOut)
        ],
        [
          t('scoringInfo.bankStatement.ownTransfersOut'),
          toTenge(statement.ownTransfersOut)
        ],
        [
          t('scoringInfo.bankStatement.purchases'),
          toTenge(statement.purchases)
        ],
        [t('scoringInfo.bankStatement.misc'), toTenge(statement.misc)]
      ]
    },
    {
      title: t('scoringInfo.bankStatement.periodSection'),
      rows: [
        [
          t('scoringInfo.bankStatement.balanceStart'),
          toTenge(statement.balanceStart)
        ],
        [
          t('scoringInfo.bankStatement.balanceEnd'),
          toTenge(statement.balanceEnd)
        ]
      ]
    },
    {
      title: t('scoringInfo.bankStatement.bankSection'),
      rows: [
        [
          t('scoringInfo.bankStatement.bank'),
          statement.bank || SYMBOLS.longDash
        ],
        [
          t('scoringInfo.bankStatement.parsedAt'),
          statement.parsedAt ? formatDate(statement.parsedAt) : SYMBOLS.longDash
        ]
      ]
    }
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={860}
      title={
        <span className="bank-statement-modal__title">
          {t('scoringInfo.bankStatement.title')}
          <Tag color={isSuccess ? 'success' : 'error'}>
            {isSuccess
              ? t('scoringInfo.bankStatement.success')
              : t('scoringInfo.bankStatement.failed')}
          </Tag>
        </span>
      }
    >
      <div className="bank-statement-modal">
        {((isSuccess && subtitle) || statement.url) && (
          <div className="bank-statement-modal__header">
            {isSuccess && subtitle && (
              <Typography.Text type="secondary">{subtitle}</Typography.Text>
            )}
            {statement.url && (
              <Button
                className="bank-statement-modal__download"
                href={statement.url}
                target="_blank"
                rel="noopener noreferrer"
                icon={<DownloadOutlined />}
                iconPlacement="end"
              >
                {t('scoringInfo.bankStatement.download')}
              </Button>
            )}
          </div>
        )}

        {isSuccess ? (
          <>
            <dl className="bank-statement-modal__summary">
              <div className="bank-statement-modal__summary-card">
                <dt className="bank-statement-modal__summary-label">
                  {t('scoringInfo.bankStatement.topUp')}
                </dt>
                <dd className="bank-statement-modal__summary-value bank-statement-modal__summary-value--success">
                  +{toTenge(Math.abs(income))}
                </dd>
              </div>
              <div className="bank-statement-modal__summary-card">
                <dt className="bank-statement-modal__summary-label">
                  {t('scoringInfo.bankStatement.expenses')}
                </dt>
                <dd className="bank-statement-modal__summary-value bank-statement-modal__summary-value--error">
                  −{toTenge(Math.abs(expenses))}
                </dd>
              </div>
              <div className="bank-statement-modal__summary-card">
                <dt className="bank-statement-modal__summary-label">
                  {t('scoringInfo.bankStatement.balanceEnd')}
                </dt>
                <dd className="bank-statement-modal__summary-value">
                  {toTenge(statement.balanceEnd)}
                </dd>
              </div>
            </dl>

            <div className="bank-statement-modal__details">
              {sections.map((section) => (
                <section key={section.title}>
                  <h3 className="bank-statement-modal__section-title">
                    {section.title}
                  </h3>
                  <dl className="bank-statement-modal__rows">
                    {section.rows.map(([label, value]) => (
                      <div key={label} className="bank-statement-modal__row">
                        <dt className="bank-statement-modal__row-label">
                          {label}
                        </dt>
                        <dd className="bank-statement-modal__row-value">
                          {value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>
              ))}
            </div>
          </>
        ) : (
          <Typography.Paragraph
            type="danger"
            strong
            className="bank-statement-modal__error"
          >
            {statement.description || t('scoringInfo.bankStatement.parseError')}
          </Typography.Paragraph>
        )}

        <div className="bank-statement-modal__footer">
          <Button onClick={onClose}>{t('common.close')}</Button>
        </div>
      </div>
    </Modal>
  );
};

export default BankStatementModal;
